import {
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  getAdminEnvironmentDetail,
  getAdminEnvironmentNames,
  type AdminEnvironmentDetail,
} from "./api";

const adminEnvironmentStaleTimeMs = 2000;
const adminEnvironmentRefetchIntervalMs = 5000;
const adminEnvironmentNameStaleTimeMs = 5 * 60 * 1000;

export const adminEnvironmentKeys = {
  all: ["devplane", "environments"] as const,
  admin: () => [...adminEnvironmentKeys.all, "admin"] as const,
  details: () => [...adminEnvironmentKeys.admin(), "detail"] as const,
  adminDetail: (environmentId: string) =>
    [...adminEnvironmentKeys.details(), environmentId] as const,
  names: () => [...adminEnvironmentKeys.admin(), "name"] as const,
  adminName: (environmentId: string) =>
    [...adminEnvironmentKeys.names(), environmentId] as const,
};

type AdminEnvironmentDetailQueryKey = ReturnType<
  typeof adminEnvironmentKeys.adminDetail
>;

export function adminEnvironmentDetailQueryOptions(
  environmentId: string,
): UseQueryOptions<
  AdminEnvironmentDetail,
  unknown,
  AdminEnvironmentDetail,
  AdminEnvironmentDetailQueryKey
> {
  return {
    queryKey: adminEnvironmentKeys.adminDetail(environmentId),
    queryFn: ({
      signal,
    }: QueryFunctionContext<AdminEnvironmentDetailQueryKey>) =>
      getAdminEnvironmentDetail(environmentId, { signal }),
    enabled: Boolean(environmentId),
    staleTime: adminEnvironmentStaleTimeMs,
    refetchInterval: adminEnvironmentRefetchIntervalMs,
    refetchIntervalInBackground: false,
  };
}

export function useAdminEnvironmentDetailQuery(environmentId: string) {
  return useQuery(adminEnvironmentDetailQueryOptions(environmentId));
}

type AdminEnvironmentNameQueryKey = ReturnType<
  typeof adminEnvironmentKeys.adminName
>;

export function adminEnvironmentNameQueryOptions(
  environmentId: string,
): UseQueryOptions<
  string | undefined,
  unknown,
  string | undefined,
  AdminEnvironmentNameQueryKey
> {
  return {
    queryKey: adminEnvironmentKeys.adminName(environmentId),
    queryFn: async ({ signal }) =>
      (await getAdminEnvironmentNames([environmentId], { signal }))[
        environmentId
      ],
    enabled: Boolean(environmentId),
    staleTime: adminEnvironmentNameStaleTimeMs,
  };
}

export function useAdminEnvironmentNameQuery(environmentId: string) {
  return useQuery(adminEnvironmentNameQueryOptions(environmentId));
}
